import { DatalayerService } from '@analytics/datalayer';
import type {
  CommerceMapperContext,
  ParsedProductTrackingConfig,
  ProductEventType,
} from '@analytics/models';
import { AnalyticsUtils } from '@analytics/utils';
import type { AfterViewInit } from '@angular/core';
import {
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import { PlatformService } from '@core/platform';
import type { GenericProductData } from '@legacy/common-ui-models';
import { InViewportConfig, InViewportService } from '@utils/viewport';

/**
 * Directive that tracks product visibility and handles product tracking events.
 *
 * Usage:
 * ```html
 * <div [wlTrackProduct]="{
 *   track: <product>,
 *   context: {
 *      index: 0,
 *      listName: 'Stroom & Gas producten',
 *      numberOfProducts: 1
 *   },
 *   trackingConfig: trackProductConfig,
 *   trackMode: 'product-impression'
 * }">
 */
@Directive({
  selector: '[wlTrackProduct]',
  standalone: true,
  providers: [InViewportService],
})
export class TrackProductDirective<
  TProduct extends GenericProductData,
  TCommerce extends Record<string, any>,
> implements AfterViewInit
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackProduct') trackProduct!: {
    track: TProduct;
    context?: CommerceMapperContext;
    trackingConfig: ParsedProductTrackingConfig<TProduct, TCommerce>;
    trackMode: Exclude<ProductEventType, 'product-click'>;
  };

  readonly #platformService = inject(PlatformService);
  readonly #elementRef = inject(ElementRef<HTMLElement>);
  readonly #inViewport = inject(InViewportService);
  readonly #datalayer = inject(DatalayerService);
  readonly #analyticsUtils = inject(AnalyticsUtils);

  // Track when element becomes visible
  private readonly elementVisible = computed(() => {
    const entries = this.#inViewport.entries();
    const visible = entries.some(
      (e) => e.target === this.#elementRef.nativeElement
    );
    const rect = this.#elementRef.nativeElement?.getBoundingClientRect();
    // Ensure our element is actually in the viewport
    return visible && rect?.x && rect?.y;
  });

  private readonly visibilityEffect = effect(() => {
    if (this.#platformService.isClient() && this.elementVisible()) {
      this.#inViewport.unregister(this.#elementRef.nativeElement);
      this.#trackProductEvent();
      // Destroy this effect, so the function can run only once
      this.visibilityEffect.destroy();
    }
  });

  ngAfterViewInit() {
    if (this.#platformService.isClient()) {
      this.#inViewport.register(
        this.#elementRef.nativeElement,
        new InViewportConfig()
      );
    }
  }
  /**
   * Maps products to commerce format and tracks appropriate event based on trackMode
   */
  #trackProductEvent() {
    const {
      trackMode = 'product-impression',
      trackingConfig,
      context = { index: 0, numberOfProducts: 1, listName: '' },
      track,
    } = this.trackProduct;

    // Map single product
    const mappedProduct = trackingConfig.mapToCommerce(track, {
      ...context,
      listName: this.#contextualizeListName(context.listName),
    });

    // Track either impression or detail
    this.#pushEventToDatalayer(trackMode, mappedProduct);
  }

  /**
   * Add site context to list names
   */
  #contextualizeListName(listName: string): string {
    if (!listName) {
      return '';
    }
    return this.#analyticsUtils.prefixWithAbbreviation(listName.toLowerCase());
  }

  /**
   * Push events to datalayer with commerce info
   */
  #pushEventToDatalayer(eventType: ProductEventType, product: TCommerce) {
    return this.#datalayer.pushEvent({
      event: eventType,
      info: { commerce: [product] },
    });
  }
}
